import { formatAmount, USD } from "@loot-os/api-contract";
import {
  parseRocaCsv,
  ROCA_BATCH_NAMES,
  ROCA_CATEGORIES,
  ROCA_CATEGORY_IDS,
  ROCA_HIGH_VALUE_CENTS,
  ROCA_PRINTINGS,
  type RocaMatchCandidate,
  type RocaMatchResult,
  type RocaSavedEntry,
  rocaHighValue,
  rocaIdToStore,
} from "@loot-os/fulfillment-core/catalog/roca-csv";
import { printingForItem } from "@loot-os/fulfillment-core/procurement/tcgplayer-url";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@loot-os/ui/components/card";
import { Field, FieldDescription, FieldLabel } from "@loot-os/ui/components/field";
import { Input } from "@loot-os/ui/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { toast } from "@loot-os/ui/components/sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@loot-os/ui/components/tabs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";

import { useTRPC } from "../../lib/trpc";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const UNSLEEVED_STEPS = [
  "Upload CSV",
  "Match Cards",
  "Pull Rejects",
  "Final Check",
  "Pull High Value Cards",
  "Save File",
] as const;

const SLEEVED_STEPS = ["Select File", "Double Check Entries", "Add to Inventory"] as const;

type Bucket = "ready" | "review" | "unmatched" | "rejected" | "high";

interface Line {
  key: string;
  line: number;
  productName: string;
  setName: string;
  tcgplayerId: string;
  rarity: string;
  number: string;
  delta: number;
  item: RocaMatchCandidate | null;
  candidates: RocaMatchCandidate[];
  candidatesCapped: boolean;
  bucket: Bucket;
}

interface GetItem {
  id: string;
  name: string;
  image: string | null;
  overrideImage: string | null;
  value: number | null;
  quantity: number;
  pricingRef: Record<string, unknown> | null;
  attributes: Record<string, unknown> | null;
  status: string;
  rocaId: string | null;
}

/** The Retool Roca CSV Parser: match while unsleeved, save the file, add once sleeved. */
export function RocaBatch() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState("unsleeved");
  const [unsleevedStep, setUnsleevedStep] = useState(0);
  const [sleevedStep, setSleevedStep] = useState(0);
  const [category, setCategory] = useState("");
  const [printing, setPrinting] = useState("");
  const [fileName, setFileName] = useState("");
  const [batchName, setBatchName] = useState("");
  const [fileIssues, setFileIssues] = useState<string[]>([]);
  const [rowIssues, setRowIssues] = useState<{ line: number; message: string }[]>([]);
  const [rows, setRows] = useState<ReturnType<typeof parseRocaCsv>["rows"]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [typedIds, setTypedIds] = useState<Record<string, string>>({});
  const [selectedNeedKey, setSelectedNeedKey] = useState<string | null>(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [selectedRejectKey, setSelectedRejectKey] = useState<string | null>(null);
  const [sleevedLines, setSleevedLines] = useState<Line[]>([]);
  const [sleevedFile, setSleevedFile] = useState("");
  const [sleevedAddedAt, setSleevedAddedAt] = useState<Date | string | null>(null);

  const saved = useQuery(trpc.platform.fulfillment.items.listRocaBatches.queryOptions());

  const match = useMutation(
    trpc.platform.fulfillment.items.matchRocaBatch.mutationOptions({
      onSuccess: (results) => {
        setLines(results.map(toLine));
        setTypedIds({});
        setSelectedNeedKey(null);
        setSelectedCandidateId(null);
        setSelectedRejectKey(null);
      },
    }),
  );
  const save = useMutation(
    trpc.platform.fulfillment.items.saveRocaBatch.mutationOptions({
      onSuccess: (result) => {
        toast.success(
          `Saved ${result.fileKey}. Open it under Sleeved Steps when the cards are sleeved.`,
        );
        void queryClient.invalidateQueries({
          queryKey: trpc.platform.fulfillment.items.listRocaBatches.pathKey(),
        });
      },
    }),
  );
  const apply = useMutation(
    trpc.platform.fulfillment.items.applyRocaBatch.mutationOptions({
      onSuccess: (result) => {
        const skipped = result.rocaIdsSkipped.length;
        toast.success(
          skipped === 0
            ? `Updated ${result.quantities} items.`
            : `Updated ${result.quantities} items. ${skipped} Roca ids already belong to another item and were left alone.`,
        );
        setSleevedLines([]);
        setSleevedFile("");
        setSleevedAddedAt(null);
        setSleevedStep(0);
        void queryClient.invalidateQueries({
          queryKey: trpc.platform.fulfillment.items.inventory.pathKey(),
        });
        void queryClient.invalidateQueries({
          queryKey: trpc.platform.fulfillment.items.listRocaBatches.pathKey(),
        });
      },
    }),
  );

  function runMatch(nextRows: typeof rows, nextPrinting: string) {
    if (!isPrinting(nextPrinting) || nextRows.length === 0) return;
    match.mutate({ printing: nextPrinting, rows: nextRows });
  }

  const ready = lines.filter((line) => line.bucket === "ready");
  const review = lines.filter((line) => line.bucket === "review");
  const high = lines.filter((line) => line.bucket === "high");
  const unmatched = lines.filter((line) => line.bucket === "unmatched");
  const rejected = lines.filter((line) => line.bucket === "rejected");
  const needs = [...review, ...unmatched];
  const needKeyList = needs.map((line) => line.key);
  const resolvedNeedKey =
    selectedNeedKey && needKeyList.includes(selectedNeedKey)
      ? selectedNeedKey
      : (needKeyList[0] ?? null);
  if (resolvedNeedKey !== selectedNeedKey) setSelectedNeedKey(resolvedNeedKey);
  const selectedNeed = needs.find((line) => line.key === resolvedNeedKey) ?? null;
  const candidateIds = selectedNeed?.candidates.map((item) => item.id) ?? [];
  const resolvedCandidateId =
    selectedCandidateId && candidateIds.includes(selectedCandidateId) ? selectedCandidateId : null;
  if (resolvedCandidateId !== selectedCandidateId) setSelectedCandidateId(resolvedCandidateId);
  const selectedReject = rejected.find((line) => line.key === selectedRejectKey) ?? null;
  const matched = [...ready, ...high].sort((a, b) =>
    (a.item?.name ?? "").localeCompare(b.item?.name ?? ""),
  );
  const sleevedReady = sleevedLines.filter((line) => line.bucket === "ready");
  const sleevedHigh = sleevedLines.filter((line) => line.bucket === "high");
  const adding = sleevedReady.reduce((sum, line) => sum + line.delta, 0);

  function choose(key: string, item: RocaMatchCandidate) {
    setLines((current) =>
      current.map((line) =>
        line.key === key
          ? { ...line, item, bucket: rocaHighValue(item.value) ? "high" : "ready" }
          : line,
      ),
    );
  }

  function reject(key: string) {
    setLines((current) =>
      current.map((line) =>
        line.key === key ? { ...line, item: null, bucket: "rejected" } : line,
      ),
    );
  }

  function setDelta(key: string, delta: number, which: "unsleeved" | "sleeved") {
    const update = (current: Line[]) =>
      current.map((line) => (line.key === key ? { ...line, delta } : line));
    if (which === "unsleeved") setLines(update);
    else setSleevedLines(update);
  }

  async function assignLine(line: Line, candidateId: string | null) {
    const picked = line.candidates.find((item) => item.id === candidateId);
    if (picked) {
      choose(line.key, picked);
      return;
    }
    const typed = typedIds[line.key]?.trim() ?? "";
    if (!UUID.test(typed)) {
      toast.error("Choose a card, or enter a catalog item id.");
      return;
    }
    try {
      const item = await queryClient.fetchQuery(
        trpc.platform.fulfillment.items.get.queryOptions({ id: typed }),
      );
      choose(line.key, candidateFromGet(item));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No catalog item has that id.");
    }
  }

  async function openSaved(fileKey: string) {
    const batch = await queryClient.fetchQuery(
      trpc.platform.fulfillment.items.getRocaBatch.queryOptions({ fileKey }),
    );
    setSleevedFile(batch.fileKey);
    setSleevedAddedAt(batch.addedAt);
    setSleevedLines(batch.entries.map(lineFromSaved));
    setSleevedStep(1);
  }

  function saveFile() {
    if (!isPrinting(printing) || !isCategory(category) || !isBatchName(batchName)) {
      toast.error("Choose a batch name.");
      return;
    }
    const entries = ready.flatMap(savedEntry);
    if (entries.length === 0) {
      toast.error("Nothing to save. High-value cards are pulled and are not stored.");
      return;
    }
    save.mutate({ fileName, batchName, category, printing, entries });
  }

  function applySaved() {
    const updates = sleevedReady.flatMap((line) => {
      if (!line.item) return [];
      const rocaId = rocaIdToStore(line.tcgplayerId, line.item.rocaId);
      if (line.delta === 0 && !rocaId) return [];
      return [{ itemId: line.item.id, delta: line.delta, rocaId }];
    });
    if (updates.length === 0) {
      toast.error("Nothing to update.");
      return;
    }
    apply.mutate({ updates, fileKey: sleevedFile });
  }

  function advanceUnsleeved() {
    if (unsleevedStep === 1) {
      setLines((current) =>
        current.map((line) =>
          line.bucket === "review" || line.bucket === "unmatched"
            ? { ...line, item: null, bucket: "rejected" }
            : line,
        ),
      );
    }
    setUnsleevedStep((step) => step + 1);
  }

  const unsleevedNextDisabled =
    unsleevedStep >= UNSLEEVED_STEPS.length - 1 ||
    (unsleevedStep === 0 && (rows.length === 0 || fileIssues.length > 0 || match.isPending));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Roca batch</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="unsleeved">Unsleeved Steps</TabsTrigger>
            <TabsTrigger value="sleeved">Sleeved Steps</TabsTrigger>
          </TabsList>

          <TabsContent value="unsleeved" className="flex flex-col gap-4">
            <FieldDescription>
              Match the sorter file while the cards are unsleeved, then save it. Add the quantities
              from Sleeved Steps once the cards are sleeved.
            </FieldDescription>
            <StepBar steps={UNSLEEVED_STEPS} index={unsleevedStep} onSelect={setUnsleevedStep} />

            {unsleevedStep === 0 ? (
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-end gap-3">
                  <Field className="w-48">
                    <FieldLabel htmlFor="roca-category">Category</FieldLabel>
                    <Select
                      value={category === "" ? undefined : category}
                      onValueChange={(value) => setCategory(value ?? "")}
                    >
                      <SelectTrigger id="roca-category" className="w-full">
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {ROCA_CATEGORIES.map((option) => (
                          <SelectItem key={option.id} value={option.id}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field className="w-52">
                    <FieldLabel htmlFor="roca-printing">Printing</FieldLabel>
                    <Select
                      value={printing === "" ? undefined : printing}
                      onValueChange={(value) => {
                        const next = value ?? "";
                        setPrinting(next);
                        setLines([]);
                        if (next && rows.length > 0) runMatch(rows, next);
                      }}
                    >
                      <SelectTrigger id="roca-printing" className="w-full">
                        <SelectValue placeholder="Select a printing" />
                      </SelectTrigger>
                      <SelectContent>
                        {ROCA_PRINTINGS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field className="min-w-52 flex-1">
                    <FieldLabel htmlFor="roca-file">CSV</FieldLabel>
                    <Input
                      id="roca-file"
                      type="file"
                      accept=".csv,text/csv"
                      disabled={category === "" || printing === "" || match.isPending}
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = "";
                        if (!file || !printing) return;
                        void file.text().then((text) => {
                          const parsed = parseRocaCsv(text);
                          setFileName(file.name);
                          setFileIssues(parsed.fileIssues);
                          setRowIssues(parsed.rowIssues);
                          setRows(parsed.rows);
                          setLines([]);
                          setUnsleevedStep(0);
                          if (parsed.fileIssues.length === 0) runMatch(parsed.rows, printing);
                        });
                      }}
                    />
                  </Field>
                </div>
                <FieldDescription>Use Holofoil for Pokemon.</FieldDescription>
                {fileName ? <p className="text-sm">{fileName}</p> : null}
                {fileIssues.map((issue) => (
                  <p key={issue} className="text-sm text-destructive" role="alert">
                    {issue}
                  </p>
                ))}
                {rowIssues.length > 0 ? (
                  <ul className="text-sm text-destructive">
                    {rowIssues.map((issue) => (
                      <li key={`${issue.line}-${issue.message}`}>
                        Line {issue.line}: {issue.message}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}

            {unsleevedStep === 1 ? (
              <div className="flex flex-col gap-3">
                {match.isPending ? (
                  <p className="text-sm text-muted-foreground">Matching…</p>
                ) : null}
                {needs.length === 0 && !match.isPending && lines.length > 0 ? (
                  <p className="text-sm">Every card already matched.</p>
                ) : null}
                {selectedNeed ? (
                  <div className="grid items-start gap-4 lg:grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <p className="text-sm font-medium">Cards to match</p>
                      <Table containerClassName="max-h-96">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Roca name</TableHead>
                            <TableHead>Number</TableHead>
                            <TableHead>Add</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {needs.map((line) => (
                            <TableRow
                              key={line.key}
                              data-state={line.key === selectedNeed.key ? "selected" : undefined}
                              className="cursor-pointer"
                              onClick={() => setSelectedNeedKey(line.key)}
                            >
                              <TableCell>
                                {line.productName}
                                {line.setName ? ` — ${line.setName}` : ""}
                              </TableCell>
                              <TableCell>{line.number}</TableCell>
                              <TableCell>{line.delta}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="flex flex-col gap-2">
                      <p className="text-sm font-medium">
                        Potential matches for {selectedNeed.productName}
                      </p>
                      {selectedNeed.candidatesCapped ? (
                        <p className="text-sm text-muted-foreground">
                          Showing the first 25 name matches.
                        </p>
                      ) : null}
                      {selectedNeed.candidates.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No catalog item matched.</p>
                      ) : (
                        <Table containerClassName="max-h-96">
                          <TableHeader>
                            <TableRow>
                              <TableHead>Name</TableHead>
                              <TableHead>Printing</TableHead>
                              <TableHead>Quantity</TableHead>
                              <TableHead>Value</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {selectedNeed.candidates.map((item) => (
                              <TableRow
                                key={item.id}
                                data-state={
                                  item.id === resolvedCandidateId ? "selected" : undefined
                                }
                                className="cursor-pointer"
                                onClick={() => setSelectedCandidateId(item.id)}
                              >
                                <TableCell>
                                  <span className="flex items-center gap-2">
                                    {item.imageUrl ? (
                                      <img
                                        src={item.imageUrl}
                                        alt=""
                                        className="h-10 w-10 object-contain"
                                      />
                                    ) : null}
                                    <span>
                                      {item.name}
                                      {item.collectionName ? ` — ${item.collectionName}` : ""}
                                      {item.archived ? " · Archived" : ""}
                                    </span>
                                  </span>
                                </TableCell>
                                <TableCell>{item.printing ?? "No printing"}</TableCell>
                                <TableCell>{item.quantity}</TableCell>
                                <TableCell>
                                  {item.value != null ? formatAmount(item.value, USD) : ""}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                      <div className="flex flex-wrap items-end gap-2">
                        <Field className="min-w-56 flex-1">
                          <FieldLabel htmlFor="roca-other-id">Other catalog item id</FieldLabel>
                          <Input
                            id="roca-other-id"
                            value={typedIds[selectedNeed.key] ?? ""}
                            onChange={(event) =>
                              setTypedIds((current) => ({
                                ...current,
                                [selectedNeed.key]: event.target.value,
                              }))
                            }
                          />
                        </Field>
                        <Button
                          type="button"
                          disabled={
                            resolvedCandidateId == null && !typedIds[selectedNeed.key]?.trim()
                          }
                          onClick={() => void assignLine(selectedNeed, resolvedCandidateId)}
                        >
                          Match Asset
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => reject(selectedNeed.key)}
                        >
                          Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {unsleevedStep === 2 ? (
              <div className="flex flex-col gap-3">
                <p className="text-sm text-muted-foreground">
                  Rejected rows are left out of the saved file. Assign a catalog item id to put one
                  back.
                </p>
                {rejected.length === 0 ? <p className="text-sm">Nothing to pull.</p> : null}
                {rejected.length > 0 ? (
                  <Table containerClassName="max-h-96">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Roca name</TableHead>
                        <TableHead>Number</TableHead>
                        <TableHead>Add</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rejected.map((line) => (
                        <TableRow
                          key={line.key}
                          data-state={line.key === selectedRejectKey ? "selected" : undefined}
                          className="cursor-pointer"
                          onClick={() => setSelectedRejectKey(line.key)}
                        >
                          <TableCell>
                            {line.productName}
                            {line.setName ? ` — ${line.setName}` : ""}
                          </TableCell>
                          <TableCell>{line.number}</TableCell>
                          <TableCell>{line.delta}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : null}
                {selectedReject ? (
                  <div className="flex flex-wrap items-end gap-2">
                    <Field className="min-w-56 flex-1">
                      <FieldLabel htmlFor="roca-reject-id">Temporary catalog item id</FieldLabel>
                      <Input
                        id="roca-reject-id"
                        value={typedIds[selectedReject.key] ?? ""}
                        onChange={(event) =>
                          setTypedIds((current) => ({
                            ...current,
                            [selectedReject.key]: event.target.value,
                          }))
                        }
                      />
                    </Field>
                    <Button
                      type="button"
                      disabled={!typedIds[selectedReject.key]?.trim()}
                      onClick={() => void assignLine(selectedReject, null)}
                    >
                      Temporarily assign
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}

            {unsleevedStep === 3 ? (
              <div className="flex flex-col gap-2">
                <p className="text-sm text-muted-foreground">
                  Check the matches and the quantities. A change is kept when you continue.
                </p>
                <ReadyTable
                  lines={matched}
                  onDelta={(key, delta) => setDelta(key, delta, "unsleeved")}
                  onReject={reject}
                />
              </div>
            ) : null}

            {unsleevedStep === 4 ? (
              <div className="flex flex-col gap-2">
                <p className="text-sm text-amber-600">
                  Worth more than {formatAmount(ROCA_HIGH_VALUE_CENTS, USD)}. Pull these. They are
                  not saved and this batch does not add them.
                </p>
                {high.length === 0 ? <p className="text-sm">No high-value cards.</p> : null}
                <ReadyTable lines={high} />
              </div>
            ) : null}

            {unsleevedStep === 5 ? (
              <div className="flex flex-col items-start gap-3">
                <p className="text-sm">
                  {fileName || "No file"} · {ready.length} cards to save
                  {high.length > 0 ? ` · ${high.length} pulled` : ""}
                </p>
                <Field className="w-56">
                  <FieldLabel htmlFor="roca-batch-name">Batch name</FieldLabel>
                  <Select
                    value={batchName === "" ? undefined : batchName}
                    onValueChange={(value) => setBatchName(value ?? "")}
                  >
                    <SelectTrigger id="roca-batch-name" className="w-full">
                      <SelectValue placeholder="Select a batch name" />
                    </SelectTrigger>
                    <SelectContent>
                      {ROCA_BATCH_NAMES.map((name) => (
                        <SelectItem key={name} value={name}>
                          {name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Button
                  type="button"
                  disabled={save.isPending || batchName === "" || ready.length === 0}
                  onClick={saveFile}
                >
                  {save.isPending ? "Saving…" : "Save for adding to Inventory Later"}
                </Button>
              </div>
            ) : null}

            <StepMove
              onPrevious={() => setUnsleevedStep((step) => Math.max(0, step - 1))}
              previousDisabled={unsleevedStep === 0}
              onNext={advanceUnsleeved}
              nextDisabled={unsleevedNextDisabled}
            />
          </TabsContent>

          <TabsContent value="sleeved" className="flex flex-col gap-4">
            <FieldDescription>
              Open a file saved from the unsleeved steps. Add it only when these are the sleeved
              cards from that file.
            </FieldDescription>
            <StepBar steps={SLEEVED_STEPS} index={sleevedStep} onSelect={setSleevedStep} />

            {sleevedStep === 0 ? (
              <div className="flex flex-col gap-2">
                {saved.isPending ? (
                  <p className="text-sm text-muted-foreground">Loading files…</p>
                ) : null}
                {saved.data && saved.data.length === 0 ? (
                  <p className="text-sm">No saved files yet.</p>
                ) : null}
                {saved.data && saved.data.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>File</TableHead>
                        <TableHead>Batch</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Printing</TableHead>
                        <TableHead>Cards</TableHead>
                        <TableHead>Saved</TableHead>
                        <TableHead>Added to inventory</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {saved.data.map((file) => (
                        <TableRow
                          key={file.fileKey}
                          data-state={file.fileKey === sleevedFile ? "selected" : undefined}
                        >
                          <TableCell>{file.fileKey}</TableCell>
                          <TableCell>{file.batchName}</TableCell>
                          <TableCell>{categoryLabel(file.category)}</TableCell>
                          <TableCell>{file.printing}</TableCell>
                          <TableCell>{file.entryCount}</TableCell>
                          <TableCell>{new Date(file.updatedAt).toLocaleString()}</TableCell>
                          <TableCell>{addedLabel(file.addedAt)}</TableCell>
                          <TableCell>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => void openSaved(file.fileKey)}
                            >
                              Open
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : null}
              </div>
            ) : null}

            {sleevedStep === 1 ? (
              <div className="flex flex-col gap-3">
                <p className="text-sm">{sleevedFile}</p>
                <AddedNote addedAt={sleevedAddedAt} />
                <ReadyTable
                  lines={sleevedReady}
                  onDelta={(key, delta) => setDelta(key, delta, "sleeved")}
                />
                {sleevedHigh.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    <p className="text-sm text-amber-600">
                      Worth more than {formatAmount(ROCA_HIGH_VALUE_CENTS, USD)}. Pull these. They
                      are not added.
                    </p>
                    <ReadyTable lines={sleevedHigh} />
                  </div>
                ) : null}
              </div>
            ) : null}

            {sleevedStep === 2 ? (
              <div className="flex flex-col items-start gap-3">
                <p className="max-w-2xl text-sm text-muted-foreground">
                  Only add these once the cards are sleeved and this file is the batch being added.
                  Cards still waiting on a match were left out when the file was saved.
                </p>
                <p className="text-sm">
                  {sleevedFile} · {sleevedReady.length} cards · {adding} to add
                </p>
                <AddedNote addedAt={sleevedAddedAt} />
                <Button type="button" disabled={apply.isPending} onClick={() => applySaved()}>
                  {apply.isPending ? "Updating…" : addLabel(adding)}
                </Button>
              </div>
            ) : null}

            <StepMove
              onPrevious={() => setSleevedStep((step) => Math.max(0, step - 1))}
              previousDisabled={sleevedStep === 0}
              onNext={() => setSleevedStep((step) => step + 1)}
              nextDisabled={sleevedStep >= SLEEVED_STEPS.length - 1 || sleevedLines.length === 0}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function StepBar({
  steps,
  index,
  onSelect,
}: {
  steps: readonly string[];
  index: number;
  onSelect: (index: number) => void;
}) {
  return (
    <ol className="flex flex-wrap gap-2">
      {steps.map((step, stepIndex) => (
        <li key={step}>
          <Button
            type="button"
            size="sm"
            variant={stepIndex === index ? "default" : "outline"}
            disabled={stepIndex > index}
            onClick={() => onSelect(stepIndex)}
          >
            {stepIndex + 1}. {step}
          </Button>
        </li>
      ))}
    </ol>
  );
}

function StepMove({
  onPrevious,
  previousDisabled,
  onNext,
  nextDisabled,
}: {
  onPrevious: () => void;
  previousDisabled: boolean;
  onNext: () => void;
  nextDisabled: boolean;
}) {
  return (
    <div className="flex gap-2">
      <Button type="button" variant="outline" disabled={previousDisabled} onClick={onPrevious}>
        Previous
      </Button>
      <Button type="button" variant="outline" disabled={nextDisabled} onClick={onNext}>
        Next
      </Button>
    </div>
  );
}

function ReadyTable({
  lines,
  onDelta,
  onReject,
}: {
  lines: Line[];
  onDelta?: (key: string, delta: number) => void;
  onReject?: (key: string) => void;
}) {
  if (lines.length === 0) return null;
  return (
    <LineTable containerClassName="max-h-96">
      {lines.map((line) => (
        <TableRow key={line.key}>
          <SorterCells line={line} />
          <TableCell>
            {onDelta ? (
              <Input
                aria-label={`Quantity for ${line.productName}`}
                className="w-20"
                type="number"
                value={line.delta}
                onChange={(event) => {
                  const next = Number.parseInt(event.target.value, 10);
                  if (Number.isInteger(next)) onDelta(line.key, next);
                }}
              />
            ) : (
              line.delta
            )}
          </TableCell>
          <ItemCell item={line.item} />
          <TableCell>
            {onReject ? (
              <Button type="button" variant="outline" size="sm" onClick={() => onReject(line.key)}>
                Remove
              </Button>
            ) : null}
          </TableCell>
        </TableRow>
      ))}
    </LineTable>
  );
}

function isPrinting(value: string): value is (typeof ROCA_PRINTINGS)[number] {
  return (ROCA_PRINTINGS as readonly string[]).includes(value);
}

function isCategory(value: string): value is (typeof ROCA_CATEGORY_IDS)[number] {
  return (ROCA_CATEGORY_IDS as readonly string[]).includes(value);
}

function isBatchName(value: string): value is (typeof ROCA_BATCH_NAMES)[number] {
  return (ROCA_BATCH_NAMES as readonly string[]).includes(value);
}

function categoryLabel(id: string): string {
  return ROCA_CATEGORIES.find((category) => category.id === id)?.label ?? id;
}

function addLabel(adding: number): string {
  return adding === 0 ? "Add to inventory" : `Add ${adding} to inventory`;
}

function addedLabel(value: Date | string | null): string {
  if (!value) return "Not yet";
  return new Date(value).toLocaleString();
}

function AddedNote({ addedAt }: { addedAt: Date | string | null }) {
  if (!addedAt) return null;
  return <p className="text-sm">This batch was added to inventory on {addedLabel(addedAt)}.</p>;
}

function bucketFor(result: RocaMatchResult): Bucket {
  if (result.item && result.highValue) return "high";
  if (result.item) return "ready";
  if (result.candidates.length > 0) return "review";
  return "unmatched";
}

function toLine(result: RocaMatchResult, index: number): Line {
  return {
    key: `${result.line}-${index}`,
    line: result.line,
    productName: result.productName,
    setName: result.setName,
    tcgplayerId: result.tcgplayerId,
    rarity: result.rarity,
    number: result.number,
    delta: result.quantity,
    item: result.item,
    candidates: result.candidates,
    candidatesCapped: result.candidatesCapped,
    bucket: bucketFor(result),
  };
}

function lineFromSaved(entry: RocaSavedEntry, index: number): Line {
  const item: RocaMatchCandidate = {
    id: entry.itemId,
    name: entry.catalogName,
    collectionName: entry.setName || null,
    printing: entry.printing,
    quantity: entry.quantity,
    value: entry.value,
    imageUrl: entry.imageUrl,
    rocaId: entry.rocaId,
    archived: entry.archived,
  };
  return {
    key: `saved-${entry.itemId}-${index}`,
    line: index + 1,
    productName: entry.productName,
    setName: entry.setName,
    tcgplayerId: entry.tcgplayerId,
    rarity: entry.rarity,
    number: entry.number,
    delta: entry.delta,
    item,
    candidates: [],
    candidatesCapped: false,
    bucket: rocaHighValue(entry.value) ? "high" : "ready",
  };
}

function savedEntry(line: Line): RocaSavedEntry[] {
  if (!line.item) return [];
  return [
    {
      itemId: line.item.id,
      delta: line.delta,
      productName: line.productName,
      setName: line.setName,
      tcgplayerId: line.tcgplayerId,
      rarity: line.rarity,
      number: line.number,
      catalogName: line.item.name,
      value: line.item.value,
      quantity: line.item.quantity,
      printing: line.item.printing,
      imageUrl: line.item.imageUrl,
      rocaId: line.item.rocaId,
      archived: line.item.archived,
    },
  ];
}

function candidateFromGet(item: GetItem): RocaMatchCandidate {
  const filter = item.attributes?.filter;
  const filterPrinting =
    filter && typeof filter === "object" ? (filter as Record<string, unknown>).printing : null;
  const collection = item.attributes?.collectionName;
  return {
    id: item.id,
    name: item.name,
    collectionName: typeof collection === "string" && collection.trim() ? collection.trim() : null,
    printing:
      printingForItem(item) ??
      (typeof filterPrinting === "string" && filterPrinting.trim() ? filterPrinting : null),
    quantity: item.quantity,
    value: item.value,
    imageUrl: item.overrideImage ?? item.image,
    rocaId: item.rocaId,
    archived: item.status === "ARCHIVED",
  };
}

function LineTable({
  children,
  containerClassName,
}: {
  children: ReactNode;
  containerClassName?: string;
}) {
  return (
    <Table containerClassName={containerClassName}>
      <TableHeader>
        <TableRow>
          <TableHead>Sorter name</TableHead>
          <TableHead>Set</TableHead>
          <TableHead>Number</TableHead>
          <TableHead>Add</TableHead>
          <TableHead>Catalog item</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>{children}</TableBody>
    </Table>
  );
}

function SorterCells({ line }: { line: Line }) {
  return (
    <>
      <TableCell>
        {line.productName}
        {line.rarity ? ` (${line.rarity})` : ""}
      </TableCell>
      <TableCell>{line.setName}</TableCell>
      <TableCell>{line.number}</TableCell>
    </>
  );
}

function ItemCell({ item }: { item: RocaMatchCandidate | null }) {
  if (!item) return <TableCell />;
  return (
    <TableCell>
      <span className="flex items-center gap-2">
        {item.imageUrl ? (
          <img src={item.imageUrl} alt="" className="h-10 w-10 object-contain" />
        ) : null}
        <Link
          to="/fulfillment/items/$itemId"
          params={{ itemId: item.id }}
          className="underline-offset-4 hover:underline"
        >
          {item.name}
        </Link>
        <span className="text-muted-foreground">
          {item.printing ?? "No printing"}
          {item.value != null ? ` · ${formatAmount(item.value, USD)}` : ""}
          {item.archived ? " · Archived" : ""} · {item.quantity} on hand
        </span>
      </span>
    </TableCell>
  );
}
